import React, { useState, type KeyboardEvent } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import { FiLock, FiAlertTriangle } from "react-icons/fi";
import { ErrorAlert } from "../../../components/ErrorAlert";
import type { useLogin } from "../hooks/useLogin";

const inputClass = "w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all bg-surface border-border text-text placeholder-text-dim focus:border-accent focus:bg-background";

interface NewPasswordViewProps {
  auth: ReturnType<typeof useLogin>;
  isDark: boolean;
}

export const NewPasswordView: React.FC<NewPasswordViewProps> = ({ auth, isDark }) => {
  const {
    newPassword,
    confirmPassword,
    error,
    isLoading,
    setNewPassword,
    setConfirmPassword,
    setError,
    handleNewPassword,
  } = auth;

  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleNewPassword();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className={`mx-auto w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 flex items-center justify-center mb-4`}>
        <FiLock size={22} />
      </div>

      <h1 className="text-2xl font-black text-center tracking-tight mb-2">Establecé tu contraseña</h1>
      <p className="text-xs text-center leading-relaxed mb-3 text-text-dim">
        Por seguridad, necesitás cambiar tu contraseña la primera vez que ingresás.
      </p>

      {error && (
        <ErrorAlert error={error} onDismiss={() => setError("")} className="mb-4" />
      )}

      {/* New Password */}
      <div className="relative">
        <input
          type={showNewPass ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Nueva contraseña"
          className={inputClass}
          disabled={isLoading}
          autoFocus
        />
        <button
          type="button"
          onClick={() => setShowNewPass(!showNewPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
        >
          {showNewPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      {/* Confirm Password */}
      <div className="relative">
        <input
          type={showConfirmPass ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Confirmar contraseña"
          className={inputClass}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPass(!showConfirmPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
        >
          {showConfirmPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <div className="text-xs leading-normal px-1 text-text-dim">
        Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número.
      </div>

      <button
        onClick={handleNewPassword}
        disabled={isLoading}
        className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98] mt-2 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Spinner />
            <span>Guardando...</span>
          </>
        ) : (
          "Confirmar contraseña"
        )}
      </button>
    </div>
  );
};
